/**
 * Pure rules behind the unified notification center (V9 design): which tab a
 * row is in, how a day's rows are laid out, how several Following updates to
 * one item merge into one row, and how the Following rows of a day fold into
 * one "Updates on things you follow" line in All. No React here, so every rule
 * is unit-tested on its own (`tests/notification-inbox.test.ts`).
 *
 * The service stores one row per notification; bundling and day sections are
 * computed here, in the browser, at read time.
 */
import {
  INBOX_APP_LABELS,
  INBOX_NOTIFICATION_TYPES,
  type InboxApp,
  type InboxBucket,
} from '@ordo/constants/notification-rules'
import type {
  InboxCounts,
  InboxNotification,
  NotificationDetailResponse,
  NotifyItem,
  NotifySender,
  SentenceSegment,
} from '@ordo/notifications-client/contract'
import type { UseNotificationsResult, UsePushSubscriptionResult } from '@ordo/notifications-client/react'

export type {
  InboxCounts,
  InboxNotification,
  NotificationDetailContent,
  NotifyItem,
  NotifySender,
  SentenceSegment,
} from '@ordo/notifications-client/contract'

export type NotificationTab = 'all' | InboxBucket

export const NOTIFICATION_TABS: readonly { id: NotificationTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'mentioned', label: 'Mentioned' },
  { id: 'following', label: 'Following' },
]

export const NOTIFICATION_BUCKET_LABELS: Record<InboxBucket, string> = {
  assigned: 'Assigned',
  mentioned: 'Mentioned',
  following: 'Following',
}

export type NotificationQuickAction = 'approve' | 'decline'

export const NOTIFICATION_ACTION_LABELS: Record<NotificationQuickAction, { label: string; past: string }> = {
  approve: { label: 'Approve', past: 'approved' },
  decline: { label: 'Decline', past: 'declined' },
}

/** One notification shown as its own row. */
export interface InboxRowEntry {
  kind: 'row'
  id: string
  notification: InboxNotification
}

/** Several Following updates to one item on one day, merged into one row. Newest first. */
export interface InboxBundleEntry {
  kind: 'bundle'
  id: string
  notifications: InboxNotification[]
}

export type InboxItemEntry = InboxRowEntry | InboxBundleEntry

/** In All, a day's 2+ Following rows folded into one "Updates on things you follow" line. */
export interface InboxGroupEntry {
  kind: 'group'
  id: string
  dayLabel: string
  entries: InboxItemEntry[]
}

export type InboxEntry = InboxItemEntry | InboxGroupEntry

export interface InboxSection {
  key: string
  label: string
  entries: InboxEntry[]
}

export type ReadState = (notification: InboxNotification) => boolean

export const isNotificationRead: ReadState = (notification) => notification.readAt !== null

export function filterNotificationsByTab(
  items: readonly InboxNotification[],
  tab: NotificationTab,
): InboxNotification[] {
  return tab === 'all' ? [...items] : items.filter((item) => item.bucket === tab)
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Local calendar day, `YYYY-MM-DD`. */
export function notificationDayKey(iso: string): string {
  const date = new Date(iso)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function dayKeyOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function shortDate(date: Date, now: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}

/** Section heading: Today, Yesterday, then the date ("Oct 14"). */
export function notificationDayLabel(dayKey: string, now: Date = new Date()): string {
  if (dayKey === dayKeyOf(now)) {
    return 'Today'
  }
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (dayKey === dayKeyOf(yesterday)) {
    return 'Yesterday'
  }
  const [year, month, day] = dayKey.split('-').map(Number)
  return shortDate(new Date(year ?? 0, (month ?? 1) - 1, day ?? 1), now)
}

/** Row time: a clock time for today and yesterday, else the date. */
export function notificationTimeLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const label = notificationDayLabel(dayKeyOf(date), now)
  if (label === 'Today' || label === 'Yesterday') {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }
  return label
}

/** The item a Following bundle is about: same app, kind and id. */
export function notificationItemKey(notification: Pick<InboxNotification, 'app' | 'item'>): string {
  return `${notification.app}:${notification.item.kind}:${notification.item.id}`
}

function newestFirst(items: readonly InboxNotification[]): InboxNotification[] {
  return [...items].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
}

/** One day's rows, with that day's Following updates to the same item merged. Order is newest first. */
function dayEntries(dayKey: string, items: InboxNotification[]): InboxItemEntry[] {
  const byItem = new Map<string, InboxNotification[]>()
  for (const item of items) {
    if (item.bucket !== 'following') {
      continue
    }
    const key = notificationItemKey(item)
    byItem.set(key, [...(byItem.get(key) ?? []), item])
  }
  const entries: InboxItemEntry[] = []
  const placed = new Set<string>()
  for (const item of items) {
    const key = notificationItemKey(item)
    const siblings = item.bucket === 'following' ? (byItem.get(key) ?? []) : []
    if (siblings.length < 2) {
      entries.push({ kind: 'row', id: item.id, notification: item })
      continue
    }
    if (placed.has(key)) {
      continue
    }
    placed.add(key)
    entries.push({ kind: 'bundle', id: `bundle:${dayKey}:${key}`, notifications: siblings })
  }
  return entries
}

export function entryIsFollowing(entry: InboxItemEntry): boolean {
  return entry.kind === 'bundle' || entry.notification.bucket === 'following'
}

/**
 * The list as it is drawn: one section per day, newest first. In All, a day
 * with 2+ Following entries folds them into one group line placed after the
 * day's other rows; a day with one keeps it as a normal row. Other tabs show
 * every row (bundles still merge).
 */
export function buildNotificationSections(
  items: readonly InboxNotification[],
  tab: NotificationTab,
  now: Date = new Date(),
): InboxSection[] {
  const days = new Map<string, InboxNotification[]>()
  for (const item of newestFirst(filterNotificationsByTab(items, tab))) {
    const key = notificationDayKey(item.createdAt)
    days.set(key, [...(days.get(key) ?? []), item])
  }
  return [...days.entries()].map(([key, dayItems]) => {
    const label = notificationDayLabel(key, now)
    const entries = dayEntries(key, dayItems)
    if (tab !== 'all') {
      return { key, label, entries }
    }
    const following = entries.filter(entryIsFollowing)
    if (following.length < 2) {
      return { key, label, entries }
    }
    // The group line sits where its newest update falls in the day, not after
    // everything else: updates newer than the day's other rows were showing at
    // the bottom of Today.
    const group: InboxEntry = { kind: 'group', id: `group:${key}`, dayLabel: label, entries: following }
    const ordered: InboxEntry[] = []
    for (const entry of entries) {
      if (!entryIsFollowing(entry)) {
        ordered.push(entry)
      } else if (!ordered.includes(group)) {
        ordered.push(group)
      }
    }
    return { key, label, entries: ordered }
  })
}

/** Every notification inside an entry (a group flattens its rows and bundles). */
export function entryNotifications(entry: InboxEntry): InboxNotification[] {
  if (entry.kind === 'row') {
    return [entry.notification]
  }
  if (entry.kind === 'bundle') {
    return entry.notifications
  }
  return entry.entries.flatMap(entryNotifications)
}

export function entryIsUnread(entry: InboxEntry, isRead: ReadState = isNotificationRead): boolean {
  return entryNotifications(entry).some((notification) => !isRead(notification))
}

/**
 * The group line's pill: how many unread updates it holds. Counted the same way
 * as the Following tab and the bell (one per unread notification), so the pill
 * and the tab agree. It used to count things with something unread, which
 * showed 3 beside a Following tab of 9.
 */
export function groupUnreadCount(group: InboxGroupEntry, isRead: ReadState = isNotificationRead): number {
  return entryNotifications(group).filter((notification) => !isRead(notification)).length
}

/** Every update inside a group line, read or not (a bundle counts each of its updates). */
export function groupUpdateCount(group: InboxGroupEntry): number {
  return entryNotifications(group).length
}

/**
 * The group line's meta: the total first so it never truncates away, then the
 * apps inside ("6 updates · Tasks · RFPs").
 */
export function groupMetaLine(group: InboxGroupEntry): string {
  const total = groupUpdateCount(group)
  return [`${total} ${total === 1 ? 'update' : 'updates'}`, ...groupAppLabels(group)].join(' · ')
}

/** The day a group line folds, as a `notificationDayKey`: the Following tab's section for the same day. */
export function groupDayKey(group: InboxGroupEntry): string {
  const first = entryNotifications(group)[0]
  return first ? notificationDayKey(first.createdAt) : group.id.replace(/^group:/, '')
}

/** The apps inside a group line, in first-seen order ("RFPs · Tasks"). */
export function groupAppLabels(group: InboxGroupEntry): string[] {
  const labels: string[] = []
  for (const notification of entryNotifications(group)) {
    const label = notificationAppLabel(notification.app)
    if (!labels.includes(label)) {
      labels.push(label)
    }
  }
  return labels
}

/** Top-level entries in drawn order, for ↑/↓ on the page. Rows inside a group line are skipped. */
export function notificationEntryOrder(sections: readonly InboxSection[]): InboxEntry[] {
  return sections.flatMap((section) => section.entries)
}

export function findNotificationEntry(sections: readonly InboxSection[], id: string | null): InboxEntry | null {
  if (!id) {
    return null
  }
  return notificationEntryOrder(sections).find((entry) => entry.id === id) ?? null
}

function senderKey(sender: NotifySender): string | null {
  if (sender.kind === 'none') {
    return null
  }
  return sender.id || sender.name
}

/** The distinct people or schools behind a set of notifications, newest first. */
export function uniqueSenders(notifications: readonly InboxNotification[]): Exclude<NotifySender, { kind: 'none' }>[] {
  const seen = new Set<string>()
  const senders: Exclude<NotifySender, { kind: 'none' }>[] = []
  for (const { sender } of notifications) {
    const key = senderKey(sender)
    if (sender.kind === 'none' || !key || seen.has(key)) {
      continue
    }
    seen.add(key)
    senders.push(sender)
  }
  return senders
}

/** "Indra Sofian and 2 others made 4 updates to Franklin Square UFSD (NY) 2025." */
export function bundleSentence(notifications: readonly InboxNotification[]): SentenceSegment[] {
  const senders = uniqueSenders(notifications)
  const label = notifications[0]?.item.label ?? ''
  const count = notifications.length
  const segments: SentenceSegment[] = []
  if (senders[0]) {
    segments.push({ text: senders[0].name, bold: true })
    if (senders.length > 1) {
      const others = senders.length - 1
      segments.push({ text: ' and ' }, { text: `${others} ${others === 1 ? 'other' : 'others'}`, bold: true })
    }
    segments.push({ text: ` made ${count} updates to ` })
  } else {
    segments.push({ text: `${count} updates to ` })
  }
  segments.push({ text: label, bold: true }, { text: '.' })
  return segments
}

const TRAILING_LINK_WORD = /\s+(for|on|to|in|of|about|at)\s*$/i

/**
 * One update inside a bundle, without the sender's name (drawn separately) or
 * the item (the bundle already names it): "secured the bid bond".
 */
export function bundleLineText(notification: InboxNotification): string {
  const segments = [...notification.sentence]
  const senderName = notification.sender.kind === 'none' ? null : notification.sender.name
  if (segments[0]?.bold && senderName && segments[0].text.trim() === senderName) {
    segments.shift()
  }
  if (segments.at(-1)?.text.trim() === '.') {
    segments.pop()
  }
  if (segments.at(-1)?.bold && segments.at(-1)?.text.trim() === notification.item.label.trim()) {
    segments.pop()
  }
  const text = segments
    .map((segment) => segment.text)
    .join('')
    .replace(TRAILING_LINK_WORD, '')
    .replace(/\.$/, '')
    .trim()
  return text || sentenceText(notification.sentence)
}

export function sentenceText(segments: readonly SentenceSegment[]): string {
  return segments.map((segment) => segment.text).join('')
}

export function notificationAppLabel(app: InboxApp | string): string {
  return (INBOX_APP_LABELS as Record<string, string>)[app] ?? app.charAt(0).toUpperCase() + app.slice(1)
}

/** "pull_request" → "pull request", for "Unfollow pull request" and "This task was deleted." */
export function itemKindLabel(kind: string): string {
  return kind.replace(/[_-]+/g, ' ').trim() || 'item'
}

function isGitHubUrl(url: string): boolean {
  try {
    return /(^|\.)github\.com$/i.test(new URL(url).hostname)
  } catch {
    return false
  }
}

/** Where "Open in {X}" goes. Always the owning app, except pull requests, which open in GitHub. */
export function openTargetName(notification: Pick<InboxNotification, 'app' | 'item'>): string {
  return isGitHubUrl(notification.item.url) ? 'GitHub' : notificationAppLabel(notification.app)
}

export function openLabel(notification: Pick<InboxNotification, 'app' | 'item'>): string {
  return `Open in ${openTargetName(notification)}`
}

/** The second link, e.g. "Open in Factory" beside "Open in GitHub". */
export function secondaryOpenLabel(item: NotifyItem): string | null {
  if (!item.secondary) {
    return null
  }
  return /^open\b/i.test(item.secondary.label) ? item.secondary.label : `Open in ${item.secondary.label}`
}

/** "Indra Sofian approved this · 8:31 AM" */
export function resolvedLine(notification: InboxNotification, now: Date = new Date()): string | null {
  if (!notification.resolved) {
    return null
  }
  const verb = NOTIFICATION_ACTION_LABELS[notification.resolved.action].past
  return `${notification.resolved.byName} ${verb} this · ${notificationTimeLabel(notification.resolved.at, now)}`
}

/** What the row says right after you answer, when the app sends no wording of its own. */
export function defaultActionOutcome(action: NotificationQuickAction): string {
  return action === 'approve' ? 'You approved this.' : 'You declined this.'
}

/** Unread counts per tab, for sources that have no server counts. */
export function countNotifications(
  items: readonly InboxNotification[],
  isRead: ReadState = isNotificationRead,
): InboxCounts {
  const counts: InboxCounts = { assigned: 0, mentioned: 0, following: 0, total: 0 }
  for (const item of items) {
    if (isRead(item)) {
      continue
    }
    counts[item.bucket] += 1
    counts.total += 1
  }
  return counts
}

export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export interface LegacyInboxInput {
  app: InboxApp
  /** The app's own type string today (Tasker `notificationType`, RFP `type`, Customers category). */
  sourceType: string
  id: string
  read: boolean
  createdAt: string
  sender: NotifySender
  sentence: SentenceSegment[]
  preview?: string | null
  item: NotifyItem
  /** Overrides the registry's default tab for this type. */
  bucket?: InboxBucket
}

/**
 * Maps an app's existing notification onto the unified shape, so the shared
 * bell can draw an app's own feed until that app reads from the
 * notifications service. The type key, tab and framework come from the inbox
 * registry (`INBOX_NOTIFICATION_TYPES`), matched on the app's own type.
 */
export function legacyInboxNotification(input: LegacyInboxInput): InboxNotification {
  const definition = INBOX_NOTIFICATION_TYPES.find(
    (type) => type.app === input.app && type.sourceType === input.sourceType,
  )
  return {
    id: input.id,
    app: input.app,
    type: definition?.key ?? `${input.app}.${input.sourceType}`,
    bucket: input.bucket ?? definition?.defaultBucket ?? 'following',
    framework: definition?.framework ?? 'generic',
    sender: input.sender,
    sentence: input.sentence,
    preview: input.preview ?? null,
    item: input.item,
    // One-click answers need the service's action route; an app's own feed has none.
    actions: [],
    resolved: null,
    readAt: input.read ? input.createdAt : null,
    createdAt: input.createdAt,
  }
}

/** The hook's result; the quick-action, reply and detail methods are optional so an app's own adapter can omit them. */
export type NotificationCenterSource = Omit<UseNotificationsResult, 'act' | 'reply' | 'loadDetail'> &
  Partial<Pick<UseNotificationsResult, 'act' | 'reply' | 'loadDetail'>>

/**
 * Props for `NotificationCenter` / `NotificationCenterPage` straight from the
 * `useNotifications()` hook in `@ordo/notifications-client/react`:
 * `<NotificationCenter {...notificationCenterPropsFrom(useNotifications({ tab }))} tab={tab} onTabChange={setTab} />`.
 * Approve/Decline, the reply box and the live right side appear only when the
 * source provides `act`, `reply` and `loadDetail`. The service's `generic`
 * detail maps to `null` (the page draws the Generic framework).
 */
export function notificationCenterPropsFrom(result: NotificationCenterSource) {
  const { act, reply, loadDetail } = result
  return {
    ...(act
      ? { onAction: (notification: InboxNotification, action: NotificationQuickAction) => act(notification, action) }
      : {}),
    ...(reply ? { onReply: (notification: InboxNotification, text: string) => reply(notification, text) } : {}),
    ...(loadDetail
      ? {
          loadDetail: async (
            notification: InboxNotification,
          ): Promise<Exclude<NotificationDetailResponse, { status: 'generic' }> | null> => {
            const detail = await loadDetail(notification)
            return detail.status === 'generic' ? null : detail
          },
        }
      : {}),
    items: result.items,
    counts: result.counts,
    loading: result.loading,
    unavailable: result.unavailable,
    hasMore: result.hasMore,
    onLoadMore: result.loadMore,
    onMarkRead: (notification: InboxNotification) => result.markRead(notification.id),
    onMarkUnread: (notification: InboxNotification) => result.markUnread(notification.id),
    onMarkAllRead: (tab: NotificationTab) => result.markAllRead(tab),
    onUnfollow: (notification: InboxNotification) => result.unfollow(notification),
    onFollowAgain: (notification: InboxNotification) => result.followAgain(notification),
  }
}

/** What the bell's "push on this device" control shows. */
export type NotificationPushStatus = 'off' | 'on' | 'blocked' | 'needs-home-screen'

/** The `pushControl` prop of `NotificationCenter` / `NotificationCenterPage`. */
export interface NotificationPushControl {
  status: NotificationPushStatus
  busy?: boolean
  /** Why the last attempt failed, shown under the control. */
  error?: string | null
  onEnable: () => void | Promise<void>
  onDisable: () => void | Promise<void>
}

/**
 * The bell's push control straight from `usePushSubscription()` in
 * `@ordo/notifications-client/react`:
 * `<NotificationCenter … pushControl={pushControlFrom(usePushSubscription())} />`.
 * Undefined (no control) when the service does not offer push on this app, or
 * the browser cannot do push and is not an iPhone that could after "Add to
 * Home Screen".
 */
export function pushControlFrom(state: UsePushSubscriptionResult): NotificationPushControl | undefined {
  if (!state.available) {
    return undefined
  }
  const base = { busy: state.busy, error: state.error, onEnable: state.subscribe, onDisable: state.unsubscribe }
  if (state.needsHomeScreen) {
    return { ...base, status: 'needs-home-screen' }
  }
  if (!state.supported) {
    return undefined
  }
  if (state.subscribed) {
    return { ...base, status: 'on' }
  }
  return { ...base, status: state.permission === 'denied' ? 'blocked' : 'off' }
}
